/* global window, CustomEvent */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../ui/logo';
import { CloseIcon, MenuIcon } from '../ui/icons';

export type HeaderVariant =
  | 'landing'
  | 'guest'
  | 'recruiter'
  | 'employer'
  | 'shared-link'
  | 'admin';

interface NavLink {
  label: string;
  href: string;
  hashLink?: boolean;
  forceTab?: string;
  primary?: boolean;
}

interface LandingHeaderProps {
  variant?: HeaderVariant;
  onLogOut?: () => void;
  onBackToAdmin?: () => void;
}

const landingLinks: NavLink[] = [
  { label: 'For Employers', href: '#employers', forceTab: 'employers' },
  { label: 'For Agencies', href: '#agencies', forceTab: 'agencies' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Contact', href: '/contact' },
  { label: 'Sign In', href: '/signin' },
  { label: 'Sign up', href: '/register', primary: true },
];

const guestLinks: NavLink[] = [
  { label: 'Pricing', href: '/#pricing', hashLink: true },
  { label: 'Contact', href: '/contact' },
  { label: 'Register', href: '/register' },
  { label: 'Sign In', href: '/signin' },
];

const authenticatedLinks: NavLink[] = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'Job Posts', href: '/job-posts' },
  { label: 'Profile', href: '/profile' },
];

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function getNavLinks(variant: HeaderVariant, _hasAdmin: boolean): NavLink[] {
  switch (variant) {
    case 'landing':
      return landingLinks;
    case 'guest':
      return guestLinks;
    case 'recruiter': {
      const links = [...authenticatedLinks];
      links.splice(2, 0, { label: 'Candidates', href: '/new-candidates' });
      return links;
    }
    case 'employer':
      return authenticatedLinks;
    case 'admin':
    case 'shared-link':
      return [];
    default:
      return landingLinks;
  }
}

function getLogoHref(variant: HeaderVariant): string {
  if (variant === 'recruiter' || variant === 'employer') return '/dashboard';
  if (variant === 'admin') return '/admin';
  return '/';
}

function isInternalLink(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('/#');
}

export function LandingHeader({
  variant = 'landing',
  onLogOut,
  onBackToAdmin,
}: LandingHeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navLinks = getNavLinks(variant, !!onBackToAdmin);
  const isAuthenticated = variant === 'recruiter' || variant === 'employer';
  const logoHref = getLogoHref(variant);

  const handleLinkClick = (link: NavLink) => {
    setIsMobileMenuOpen(false);
    if (link.forceTab) {
      window.dispatchEvent(new CustomEvent('force-tab', { detail: link.forceTab }));
    }
  };

  const renderDesktopLink = (link: NavLink) => {
    const onClick = () => handleLinkClick(link);

    if (link.primary) {
      if (isInternalLink(link.href)) {
        return (
          <Link
            key={link.label}
            to={link.href}
            onClick={onClick}
            className="px-6 py-2.5 rounded-full bg-tertiary text-white hover:bg-tertiary/90 transition shadow-md shadow-tertiary/30 hover:shadow-lg hover:shadow-tertiary/40 active:scale-95 duration-300 text-sm font-bold tracking-wide"
          >
            {link.label}
          </Link>
        );
      }
      return (
        <a
          key={link.label}
          href={link.href}
          onClick={onClick}
          className="px-6 py-2.5 rounded-full bg-tertiary text-white hover:bg-tertiary/90 transition shadow-md shadow-tertiary/30 hover:shadow-lg hover:shadow-tertiary/40 active:scale-95 duration-300 text-sm font-bold tracking-wide"
        >
          {link.label}
        </a>
      );
    }

    if (isInternalLink(link.href)) {
      return (
        <Link
          key={link.label}
          to={link.href}
          onClick={onClick}
          className="text-slate-200 hover:text-white transition-colors duration-300 text-sm font-semibold"
        >
          {link.label}
        </Link>
      );
    }
    return (
      <a
        key={link.label}
        href={link.href}
        onClick={onClick}
        className="text-slate-200 hover:text-white transition-colors duration-300 text-sm font-semibold"
      >
        {link.label}
      </a>
    );
  };

  const renderMobileLink = (link: NavLink) => {
    const onClick = () => handleLinkClick(link);

    if (link.primary) {
      if (isInternalLink(link.href)) {
        return (
          <Link
            key={link.label}
            to={link.href}
            onClick={onClick}
            className="w-full text-center px-5 py-3 rounded-full bg-tertiary text-white hover:bg-tertiary/90 transition shadow-md active:scale-95 duration-0 font-semibold hover:shadow-tertiary/40"
          >
            {link.label}
          </Link>
        );
      }
      return (
        <a
          key={link.label}
          href={link.href}
          onClick={onClick}
          className="w-full text-center px-5 py-3 rounded-full bg-tertiary text-white hover:bg-tertiary/90 transition shadow-md active:scale-95 duration-0 font-semibold hover:shadow-tertiary/40"
        >
          {link.label}
        </a>
      );
    }

    if (isInternalLink(link.href)) {
      return (
        <Link
          key={link.label}
          to={link.href}
          onClick={onClick}
          className="text-slate-200 hover:text-white transition-colors text-base font-semibold"
        >
          {link.label}
        </Link>
      );
    }
    return (
      <a
        key={link.label}
        href={link.href}
        onClick={onClick}
        className="text-slate-200 hover:text-white transition-colors text-base font-semibold"
      >
        {link.label}
      </a>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-20 bg-primary text-white backdrop-blur-md py-4 shadow-lg">
      <div className="container mx-auto px-6 flex items-center justify-between">
        <Logo href={logoHref} variant="white" />
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map(renderDesktopLink)}

          {onBackToAdmin && (
            <button
              type="button"
              onClick={onBackToAdmin}
              className="text-slate-200 hover:text-white transition-colors duration-300 text-sm font-semibold cursor-pointer bg-transparent border-none"
            >
              Back to Admin
            </button>
          )}

          {onLogOut && (
            <button
              type="button"
              onClick={onLogOut}
              className="text-slate-200 hover:text-white transition-colors duration-300 text-sm font-semibold cursor-pointer bg-transparent border-none"
            >
              Log Out
            </button>
          )}
        </nav>
        {(navLinks.length > 0 || isAuthenticated) && (
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white transition-colors hover:text-slate-200 p-2 -mr-2"
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <CloseIcon className="w-6 h-6" />
            ) : (
              <MenuIcon className="w-6 h-6" />
            )}
          </button>
        )}
      </div>
      {isMobileMenuOpen && (
        <div
          id="mobile-menu"
          className="lg:hidden absolute top-full left-0 w-full bg-primary border-b border-primary shadow-xl py-6 px-6 flex flex-col gap-6 animate-in slide-in-from-top-2 duration-200"
        >
          {navLinks.filter(l => !l.primary).map(renderMobileLink)}

          {onBackToAdmin && (
            <button
              type="button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                onBackToAdmin();
              }}
              className="text-slate-200 hover:text-white transition-colors text-base font-semibold cursor-pointer bg-transparent border-none text-left"
            >
              Back to Admin
            </button>
          )}

          {(navLinks.some(l => l.primary) || isAuthenticated) && (
            <>
              <hr className="border-white/10" />
              <div className="flex flex-col gap-3">
                {navLinks.filter(l => l.primary).map(renderMobileLink)}

                {onLogOut && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      onLogOut();
                    }}
                    className="w-full text-center px-5 py-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors font-semibold active:scale-95 duration-0 cursor-pointer border-none"
                  >
                    Log Out
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </header>
  );
}
